"use client";
import React, { useEffect, useState, useRef } from "react";
import DynamicWizard, { WizardStep } from "@/components/dynamicWizard";
import { WizardService } from "@/services/wizard";
import { BASE_URL } from "@/services/base";
import ReactMarkdown from "react-markdown";

interface WizProps {
  slug?: string;
}

interface Message {
  role: "user" | "assistant";
  text: string;
  isError?: boolean; // Added for retry logic
}

const WizardQuiz: React.FC<WizProps> = ({ slug = "default" }) => {
  const [steps, setSteps] = useState<WizardStep[]>([]);
  const [isChatMode, setIsChatMode] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (isChatMode) {
      scrollToBottom();
    }
  }, [messages, streamingText, isThinking, isChatMode]);

  const triggerChat = async (messageText: string) => {
    if (!messageText.trim() || isLoading) return;

    // Check if we are retrying (avoid adding duplicate user messages to the UI)
    const isRetry = messages.length > 0 && messages[messages.length - 1].isError;

    if (!isRetry) {
      setMessages((prev) => [...prev, { role: "user", text: messageText }]);
    } else {
      // If retrying, remove the previous error message to keep the UI clean
      setMessages((prev) => prev.slice(0, -1));
    }

    setQuery("");
    setIsLoading(true);
    setIsThinking(true);
    setStreamingText("");

    try {
      const response = await fetch(`${BASE_URL}/llm/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: messageText }),
      });

      if (!response.ok) throw new Error("Session expired or server error");

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder();
      let fullText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        if (fullText === "") setIsThinking(false);

        fullText += chunk;
        setStreamingText(fullText);
      }

      setMessages((prev) => [...prev, { role: "assistant", text: fullText }]);
      setStreamingText("");

    } catch (err: any) {
      console.error(err);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: `Error: ${err.message || "System is currently busy."}`,
          isError: true
        }
      ]);
    } finally {
      setIsLoading(false);
      setIsThinking(false);
    }
  };

  const handleComplete = async (answers: Record<string, string | string[]>) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/llm/ai/setup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers, category: slug }),
      });

      if (response.ok) {
        const data = await response.json();
        setMessages([{ role: "assistant", text: data.message || "Setup complete!" }]);
        setIsChatMode(true);
        triggerChat("analyze and recommend");
      } else {
        console.error("Failed to setup AI:", response.statusText);
      }
    } catch (error) {
      console.error("Error connecting to server:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendQuery = () => {
    triggerChat(query);
  };

  useEffect(() => {
    const wizardFetch = async () => {
      const stp = await WizardService.wizardCatagoryList(slug);
      setSteps(stp);
    };
    wizardFetch();
  }, [slug]);

  if (isChatMode) {
    return (
      <div className="flex flex-col w-full max-w-4xl mx-auto h-[600px] border rounded-lg shadow-sm bg-white overflow-hidden">
        <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((msg, idx) => (
            <div key={`msg-${idx}`} className={`flex ${msg.role === "assistant" ? "justify-start" : "justify-end"}`}>
              <div className={`max-w-[80%] rounded-2xl px-5 py-3 ${msg.role === "assistant" ? "bg-gray-100 text-gray-800" : "bg-[#D4AF37] text-white"}`}>
                <div className="text-sm space-y-2 prose prose-sm max-w-none">
                  <ReactMarkdown>{msg.text}</ReactMarkdown>
                </div>

                {/* RETRY BUTTON */}
                {msg.isError && (
                  <button
                    disabled={isLoading}
                    onClick={() => {
                      const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');
                      if (lastUserMsg) triggerChat(lastUserMsg.text);
                    }}
                    className="mt-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {isLoading ? "Retrying..." : "Retry"}
                  </button>
                )}
              </div>
            </div>
          ))}

          {isThinking && !streamingText && (
            <div className="flex justify-start">
              <div className="max-w-[80%] rounded-2xl px-5 py-3 bg-gray-100 text-gray-800 flex items-center gap-3">
                <span className="text-sm text-gray-500 italic">OnlyLife AI is thinking...</span>
              </div>
            </div>
          )}

          {streamingText && (
            <div className="flex justify-start">
              <div className="max-w-[80%] rounded-2xl px-5 py-3 bg-gray-100 text-gray-800">
                <div className="text-sm space-y-2 prose prose-sm max-w-none">
                  <ReactMarkdown>{streamingText}</ReactMarkdown>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-gray-50 flex items-center gap-2">
          <input
            type="text"
            className="flex-1 px-4 py-2 border rounded-full focus:outline-none focus:ring-2 focus:ring-[#D4AF37] disabled:bg-gray-100"
            placeholder="Type your message..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSendQuery()}
            disabled={isLoading}
          />
          <button
            onClick={handleSendQuery}
            disabled={isLoading || !query.trim()}
            className="px-6 py-2 bg-[#D4AF37] text-white rounded-full hover:bg-[#b0902a] transition-colors disabled:opacity-50 flex items-center justify-center min-w-[100px]"
          >
            {isLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : "Send"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/50 backdrop-blur-sm rounded-lg">
          <div className="w-8 h-8 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      <DynamicWizard steps={steps} onComplete={handleComplete} />
    </div>
  );
};

export default WizardQuiz;
